import {
  ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuTrigger,
  ContextMenuSeparator, ContextMenuSub, ContextMenuSubContent, ContextMenuSubTrigger,
} from "@/components/ui/context-menu";
import { useDocumentTabs } from "@/components/DocumentTabs";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ExternalLink, Edit3, Copy, FolderInput, Pin, Trash2, BookOpen, MousePointerClick,
} from "lucide-react";

/**
 * Entity context menu — wraps any entity card/row.
 *
 * Required:  children
 * Optional callbacks (omit to hide that menu item):
 *   onOpen, onRename, onDuplicate, onDelete
 * Optional metadata:
 *   detailPath  — string URL to open in a new tab
 *   entityName  — used as tab label
 *   pinPath     — short path like "npcs" for sidebar pin (defaults to current section)
 *   pinLabel    — display label for the pin entry
 *   folders     — [{id, name}] to populate "Move to folder" submenu
 *   currentFolderId — currently-assigned folder id
 *   onMoveFolder — (folderId | null) => void
 */
export const EntityContextMenu = ({
  children,
  onOpen,
  onRename,
  onDuplicate,
  onDelete,
  detailPath,
  entityName,
  pinPath,
  pinLabel,
  folders,
  currentFolderId,
  onMoveFolder,
  asChild = true,
}) => {
  const { campaignId } = useParams();
  const { openTab } = useDocumentTabs();

  const handleOpenInNewTab = () => {
    if (!detailPath) { toast.error("No detail page for this entity"); return; }
    openTab(detailPath, entityName);
  };

  const handlePin = () => {
    if (!campaignId || !pinPath) return;
    try {
      const key = `np_pinned_${campaignId}`;
      const cur = JSON.parse(localStorage.getItem(key)) || [];
      const entry = { path: pinPath, label: pinLabel || entityName || "Item" };
      const exists = cur.some(p => p.label === entry.label);
      const next = exists ? cur.filter(p => p.label !== entry.label) : [entry, ...cur].slice(0, 6);
      localStorage.setItem(key, JSON.stringify(next));
      window.dispatchEvent(new Event("np-pinned-changed"));
      toast.success(exists ? "Unpinned" : "Pinned to sidebar");
    } catch {}
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild={asChild} data-testid="entity-context-menu-trigger">
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56" data-testid="entity-context-menu">
        {onOpen && (
          <ContextMenuItem onClick={onOpen} data-testid="ctx-open">
            <MousePointerClick className="w-3.5 h-3.5 mr-2" /> Open
          </ContextMenuItem>
        )}
        {detailPath && (
          <ContextMenuItem onClick={handleOpenInNewTab} data-testid="ctx-open-new-tab">
            <ExternalLink className="w-3.5 h-3.5 mr-2" /> Open in New Tab
          </ContextMenuItem>
        )}
        {(onOpen || detailPath) && <ContextMenuSeparator />}
        {onRename && (
          <ContextMenuItem onClick={onRename} data-testid="ctx-rename">
            <Edit3 className="w-3.5 h-3.5 mr-2" /> Rename / Edit
          </ContextMenuItem>
        )}
        {onDuplicate && (
          <ContextMenuItem onClick={onDuplicate} data-testid="ctx-duplicate">
            <Copy className="w-3.5 h-3.5 mr-2" /> Duplicate
          </ContextMenuItem>
        )}
        {folders && folders.length > 0 && onMoveFolder && (
          <ContextMenuSub>
            <ContextMenuSubTrigger data-testid="ctx-move-folder">
              <FolderInput className="w-3.5 h-3.5 mr-2" /> Move to folder
            </ContextMenuSubTrigger>
            <ContextMenuSubContent className="w-48">
              <ContextMenuItem
                onClick={() => onMoveFolder(null)}
                disabled={!currentFolderId}
                data-testid="ctx-move-folder-none"
              >
                <span className="text-xs italic text-muted-foreground">Unfiled</span>
              </ContextMenuItem>
              <ContextMenuSeparator />
              {folders.map(f => (
                <ContextMenuItem
                  key={f.id}
                  onClick={() => onMoveFolder(f.id)}
                  disabled={currentFolderId === f.id}
                  data-testid={`ctx-move-folder-${f.id}`}
                >
                  <BookOpen className="w-3.5 h-3.5 mr-2" /> {f.name}
                </ContextMenuItem>
              ))}
            </ContextMenuSubContent>
          </ContextMenuSub>
        )}
        {pinPath && (
          <ContextMenuItem onClick={handlePin} data-testid="ctx-pin">
            <Pin className="w-3.5 h-3.5 mr-2" /> Pin to Sidebar
          </ContextMenuItem>
        )}
        {onDelete && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onClick={onDelete} className="text-destructive focus:text-destructive" data-testid="ctx-delete">
              <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};

export default EntityContextMenu;
